import React, { useState, useEffect } from 'react';
import styles from './InstagramFeed.module.css';

/**
 * InstagramFeed Component
 *
 * Displays a grid of Instagram posts fetched from the Meta Graph API via Netlify Function
 *
 * @param {Object} props
 * @param {number} props.limit - Number of posts to display (default: 9)
 */
const InstagramFeed = ({ limit = 9 }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchInstagramFeed = async () => {
      try {
        const response = await fetch(
          `/.netlify/functions/instagram-feed?limit=${limit}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch Instagram feed');
        }

        const data = await response.json();
        setPosts(data.items || []);
        setLoading(false);
      } catch (err) {
        console.error('Error loading Instagram feed:', err);
        setError(true);
        setLoading(false);
      }
    };

    fetchInstagramFeed();
  }, [limit]);

  // Truncate caption text for alt attribute
  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'Instagram post';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <p>Loading Instagram feed...</p>
        </div>
      </div>
    );
  }

  // Error state - fail silently with minimal feedback
  if (error || posts.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.fallback}>
          <p>Unable to load Instagram feed at this time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.grid}>
        {posts.map((post) => (
          <a
            key={post.id}
            href={post.permalink}
            target="_blank"
            rel="noreferrer"
            className={styles.gridItem}
            aria-label={`View Instagram post: ${truncateText(post.caption, 50)}`}
          >
            <div className={styles.imageWrapper}>
              <img
                src={post.mediaUrl}
                alt={truncateText(post.caption)}
                loading="lazy"
                className={styles.image}
              />
              <div className={styles.overlay}>
                <div className={styles.overlayContent}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={styles.icon}
                  >
                    <path
                      d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className={styles.viewText}>View on Instagram</span>
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default InstagramFeed;
